import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  RemoveCircleOutline as ShortCloseIcon,
} from "@mui/icons-material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import {
  ShortClosePoLineInfo,
  ShortClosePoLineInput,
} from "../../../../../models/PurchaseRequest/PurchaseRequestProductRequestBody";

interface ShortClosePoDialogProps {
  open: boolean;
  prId: number | null;
  poId: number | null;
  lines: ShortClosePoLineInfo[];
  loading: boolean;
  preselectIds: number[];
  onClose: () => void;
  onSubmit: (lines: ShortClosePoLineInput[], reason: string) => Promise<void>;
}

const validateCloseQty = (
  line: ShortClosePoLineInfo,
  raw: string,
): string | null => {
  if (raw.trim() === "") return "Enter the qty to close this item at.";
  const qty = Number(raw);
  if (!Number.isInteger(qty)) return "Close at must be a whole number.";
  // Close at = kept qty: inwarded <= close at <= ordered − 1.
  if (qty < line.inwarded_quantity)
    return `${line.inwarded_quantity} qty already inwarded for this item, so it can't be closed below ${line.inwarded_quantity}.`;
  if (qty >= line.ordered_quantity)
    return `Close at must be less than the PR qty (${line.ordered_quantity}). To keep the full qty, unselect this item.`;
  return null;
};

const ShortClosePoDialog: React.FC<ShortClosePoDialogProps> = ({
  open,
  prId,
  poId,
  lines,
  loading,
  preselectIds,
  onClose,
  onSubmit,
}) => {
  const theme = useTheme();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [closeQty, setCloseQty] = useState<Record<number, string>>({});
  const [submitErrors, setSubmitErrors] = useState<Record<number, string>>({});
  const [reason, setReason] = useState("");
  const [reasonTouched, setReasonTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const eligibleLines = useMemo(
    () => lines.filter((line) => line.eligible),
    [lines],
  );

  useEffect(() => {
    if (!open) return;
    const ids = eligibleLines.map((line) => line.sku_variant_id);
    setSelected(
      new Set(
        preselectIds.length > 0
          ? ids.filter((id) => preselectIds.includes(id))
          : ids,
      ),
    );
    setCloseQty(
      Object.fromEntries(
        eligibleLines.map((line) => [line.sku_variant_id, ""]),
      ),
    );
    setSubmitErrors({});
    setReason("");
    setReasonTouched(false);
  }, [open, eligibleLines, preselectIds]);

  const errors = useMemo(() => {
    const result: Record<number, string | null> = {};
    for (const line of eligibleLines) {
      if (selected.has(line.sku_variant_id)) {
        result[line.sku_variant_id] = validateCloseQty(
          line,
          closeQty[line.sku_variant_id] ?? "",
        );
      }
    }
    return result;
  }, [eligibleLines, selected, closeQty]);

  const totalShortQty = eligibleLines.reduce((sum, line) => {
    const id = line.sku_variant_id;
    if (!selected.has(id) || errors[id]) return sum;
    return sum + (line.ordered_quantity - Number(closeQty[id]));
  }, 0);
  const reasonError =
    reasonTouched && reason.trim().length === 0
      ? "Reason is required"
      : reason.trim().length > 255
        ? "Max 255 characters"
        : null;
  const canSubmit =
    !loading &&
    !submitting &&
    selected.size > 0 &&
    reason.trim().length > 0 &&
    reason.trim().length <= 255;

  const allSelected =
    eligibleLines.length > 0 && selected.size === eligibleLines.length;

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    // Validate only on submit; show errors under each item's row.
    const found: Record<number, string> = {};
    for (const [id, message] of Object.entries(errors)) {
      if (message) found[Number(id)] = message;
    }
    setSubmitErrors(found);
    if (Object.keys(found).length > 0) return;
    setSubmitting(true);
    try {
      await onSubmit(
        eligibleLines
          .filter((line) => selected.has(line.sku_variant_id))
          .map((line) => ({
            sku_variant_id: line.sku_variant_id,
            close_qty: Number(closeQty[line.sku_variant_id]),
          })),
        reason.trim(),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const headCellSx = {
    fontWeight: 600,
    fontSize: "12px",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.customColors?.grayBackGroundColor,
    whiteSpace: "nowrap" as const,
    py: 1,
  };
  const sectionSx = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
    px: 2,
    py: 1.25,
    backgroundColor: theme.palette.customColors?.grayBackGroundColor,
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors?.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
      sx={{ zIndex: 2200 }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          maxHeight: "90vh",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors?.blueGradientStart} 0%, ${theme.palette.customColors?.blueGradientEnd} 50%, ${theme.palette.customColors?.blueGradientStart} 100%)`,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors?.greyBorder || theme.palette.divider}`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: theme.palette.primary.main + "15",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ShortCloseIcon sx={{ color: "primary.main", fontSize: 20 }} />
            </Box>
            <Box>
              <CustomTypography
                variant="heading4"
                fontWeight={600}
                color={
                  theme.palette.customColors?.blackBorder || "text.primary"
                }
              >
                Short Close{poId ? ` PO #${poId}` : ""}
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={
                  theme.palette.customColors?.greyTextColor || "text.secondary"
                }
                sx={{ mt: 0.5 }}
              >
                {prId ? `PR #${prId} · ` : ""}Close items at the entered qty.
                Closed items accept inward only up to the closed qty.
              </CustomTypography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={submitting}
            aria-label="Close short close"
            sx={{
              color:
                theme.palette.customColors?.greyTextColor ||
                theme.palette.text.secondary,
              "&:hover": {
                backgroundColor:
                  theme.palette.customColors?.lightGreyColor ||
                  theme.palette.grey[100],
              },
              "&:disabled": { opacity: 0.5 },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 4,
                justifyContent: "center",
              }}
            >
              <CircularProgress size={18} />
              <CustomTypography
                variant="body3"
                color={theme.palette.text.secondary}
              >
                Loading PO items...
              </CustomTypography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  ...sectionSx,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: { xs: 1.5, sm: 4 },
                  mb: 2,
                }}
              >
                {[
                  [
                    "Items selected",
                    `${selected.size} / ${eligibleLines.length}`,
                  ],
                  ["Total short qty", String(totalShortQty)],
                ].map(([label, value]) => (
                  <Box key={label}>
                    <CustomTypography
                      variant="body3"
                      color={theme.palette.text.secondary}
                    >
                      {label}
                    </CustomTypography>
                    <CustomTypography
                      variant="heading6"
                      fontWeight={700}
                      color={
                        label === "Total short qty"
                          ? theme.palette.error.main
                          : undefined
                      }
                    >
                      {value}
                    </CustomTypography>
                  </Box>
                ))}
              </Box>

              <TableContainer
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  maxHeight: "45vh",
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={headCellSx}>
                        <Checkbox
                          size="small"
                          checked={allSelected}
                          indeterminate={selected.size > 0 && !allSelected}
                          disabled={eligibleLines.length === 0 || submitting}
                          onChange={() =>
                            setSelected(
                              allSelected
                                ? new Set()
                                : new Set(
                                    eligibleLines.map(
                                      (line) => line.sku_variant_id,
                                    ),
                                  ),
                            )
                          }
                        />
                      </TableCell>
                      <TableCell sx={headCellSx}>Item / SKU</TableCell>
                      <TableCell sx={headCellSx} align="center">
                        Ordered
                      </TableCell>
                      <TableCell sx={headCellSx} align="center">
                        Inwarded
                      </TableCell>
                      <TableCell sx={headCellSx} align="center">
                        Close at
                      </TableCell>
                      <TableCell sx={headCellSx} align="center">
                        Short qty
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {eligibleLines.map((line) => {
                      const id = line.sku_variant_id;
                      const isSelected = selected.has(id);
                      const qtyText = closeQty[id] ?? "";
                      const error = errors[id];
                      const submitError = submitErrors[id];
                      const muted = !line.eligible;
                      const textColor = muted
                        ? theme.palette.text.disabled
                        : undefined;
                      return (
                        <React.Fragment key={id}>
                          <TableRow
                            hover={!muted}
                            sx={{ "& td": { py: 0.75, color: textColor } }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                disabled={muted || submitting}
                                onChange={() => toggle(id)}
                              />
                            </TableCell>
                            <TableCell>
                              <CustomTypography
                                variant="body2"
                                fontWeight={600}
                                color={textColor}
                              >
                                {line.title || line.product_name || line.sku}
                              </CustomTypography>
                              <CustomTypography
                                variant="body3"
                                color={theme.palette.text.secondary}
                              >
                                {line.sku}
                              </CustomTypography>
                              {line.ineligible_reason && (
                                <CustomTypography
                                  variant="body3"
                                  color={theme.palette.text.secondary}
                                  sx={{ display: "block", fontStyle: "italic" }}
                                >
                                  {line.ineligible_reason}
                                  {line.short_closed_qty != null
                                    ? ` at ${line.short_closed_qty}`
                                    : ""}
                                </CustomTypography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {line.ordered_quantity}
                            </TableCell>
                            <TableCell align="center">
                              {line.inwarded_quantity}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                width: 120,
                                "& > div": { mx: "auto" },
                                "& input": { textAlign: "center" },
                              }}
                            >
                              {muted ? (
                                (line.short_qty ?? "—")
                              ) : (
                                <CustomTextField
                                  type="number"
                                  allowNegativeValue={false}
                                  allowDecimalValue={false}
                                  placeholder="00"
                                  width={90}
                                  value={qtyText}
                                  disabled={!isSelected || submitting}
                                  onChange={(event) => {
                                    setCloseQty((prev) => ({
                                      ...prev,
                                      [id]: event.target.value,
                                    }));
                                    setSubmitErrors((prev) => {
                                      if (!prev[id]) return prev;
                                      const next = { ...prev };
                                      delete next[id];
                                      return next;
                                    });
                                  }}
                                />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <CustomTypography
                                variant="body2"
                                fontWeight={700}
                                color={theme.palette.error.main}
                              >
                                {line.short_closed_qty != null
                                  ? (line.short_qty ?? "-")
                                  : isSelected && !error
                                    ? line.ordered_quantity - Number(qtyText)
                                    : "-"}
                              </CustomTypography>
                            </TableCell>
                          </TableRow>
                          {submitError && (
                            <TableRow>
                              <TableCell colSpan={6} sx={{ py: 0.5 }}>
                                <CustomTypography
                                  variant="body3"
                                  color={theme.palette.error.main}
                                >
                                  {submitError}
                                </CustomTypography>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {eligibleLines.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                          <CustomTypography
                            variant="body3"
                            color={theme.palette.text.secondary}
                          >
                            No items available to short close on this PO.
                          </CustomTypography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 2.5 }} />
              <Box>
                <CustomTypography
                  variant="body2"
                  fontWeight={600}
                  sx={{ mb: 0.75 }}
                >
                  Reason for short close *
                </CustomTypography>
                <CustomTextField
                  placeholder="Enter reason for short close"
                  width="100%"
                  value={reason}
                  onChange={(event) => {
                    setReason(event.target.value.slice(0, 255));
                    setReasonTouched(true);
                  }}
                  disabled={submitting}
                  borderColor={
                    reasonError ? theme.palette.error.main : undefined
                  }
                  helperText={reasonError ?? ""}
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            padding: { xs: "10px 16px 10px", sm: "16px 20px 20px" },
            borderTop: `1px solid ${theme.palette.customColors?.greyBorder || theme.palette.divider}`,
            backgroundColor: theme.palette.customColors?.lightGreyColor,
            justifyContent: "flex-end",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <AppButton
            onClick={onClose}
            variant="grayscale"
            label="Cancel"
            disabled={submitting}
            customStyles={{
              borderColor: theme.palette.customColors?.greyDarkborder,
              color: theme.palette.customColors?.greyTextColor,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "8px",
            }}
          />
          <AppButton
            variant="primary"
            label={
              submitting
                ? "Closing..."
                : `Short close${selected.size ? ` ${selected.size} item${selected.size === 1 ? "" : "s"}` : ""}`
            }
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          />
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ShortClosePoDialog;
